import { InputBase, Popper, Typography, ClickAwayListener } from '@mui/material'
import { styled, keyframes } from '@mui/system'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { colorPalette } from '../../../../../style/colorPalette'
import { Checkbox } from '../../../../../components/inputs/Checkbox'
import { SelectOption } from '../../../../../model/selectOption'
import { Button } from '../../../../../components/buttons'
import { intl } from '../../../../../../i18n'

type Props = {
  open: boolean
  anchorEl: Element | undefined
  options: SelectOption[]
  isLoading?: boolean
  temporarySelection: Set<string>
  onToggleItem: (value: string) => void
  onApply: () => void
  onClose: () => void
}

const ProcessScopeSearchSelectPopper = ({
  open,
  anchorEl,
  options,
  isLoading,
  temporarySelection,
  onToggleItem,
  onApply,
  onClose,
}: Props) => {
  const [ready, setReady] = useState(false)
  const [filterText, setFilterText] = useState('')
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    const timerId = setTimeout(() => setReady(true), 0)
    return () => {
      clearTimeout(timerId)
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [])

  const handleSearchTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(() => {
        setFilterText(value)
      }, 300)
    },
    []
  )

  const filteredOptions = useMemo(() => {
    if (!filterText) return options
    const lower = filterText.toLowerCase()
    return options.filter(option => option.name.toLowerCase().includes(lower))
  }, [options, filterText])
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      sx={{
        zIndex: 1301,
        background: colorPalette.monotone[0],
        boxShadow:
          'rgb(0 0 0 / 20%) 0px 5px 5px -3px, rgb(0 0 0 / 14%) 0px 8px 10px 1px, rgb(0 0 0 / 12%) 0px 3px 14px 2px',
        borderRadius: '4px',
        padding: '10px',
        minWidth: '445px',
      }}
      placement="bottom-start"
      modifiers={[
        {
          name: 'offset',
          options: { offset: [0, 20] },
        },
        {
          name: 'flip',
          enabled: true,
          options: {
            altBoundary: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altAxis: true,
            altBoundary: true,
            tether: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
      ]}
      disablePortal={true}
    >
      <ClickAwayListener onClickAway={onClose}>
        <PopperContent>
          <InputBase
            autoFocus={true}
            placeholder={intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.processScope.searchPlaceholder',
            })}
            onChange={handleSearchTextChange}
            sx={{
              border: `1px solid ${colorPalette.monotone[2]}`,
              borderRadius: '4px',
              padding: '4px 8px',
              marginBottom: '8px',
              fontSize: '14px',
            }}
          />
          <OptionsList>
            {!ready || isLoading ? (
              <LoadingContainer>
                <Spinner />
              </LoadingContainer>
            ) : (
              filteredOptions.map(option => (
                <OptionItem
                  key={option.value}
                  onClick={() => onToggleItem(option.value)}
                >
                  <Checkbox
                    sx={{ marginRight: '8px' }}
                    checked={temporarySelection.has(option.value)}
                  />
                  {option.iconUrl && <img src={option.iconUrl} />}
                  <Typography
                    sx={{
                      margin: 'auto 10px',
                      color: colorPalette.monotone[4],
                    }}
                  >
                    {option.name}
                  </Typography>
                </OptionItem>
              ))
            )}
          </OptionsList>
          <ApplyButtonContainer>
            <Button
              colorPattern="skyBlue"
              variant="filled"
              size="s"
              onClick={onApply}
            >
              {intl.formatMessage({
                id: 'savedUIState.content.button.apply',
              })}
            </Button>
          </ApplyButtonContainer>
        </PopperContent>
      </ClickAwayListener>
    </Popper>
  )
}

const PopperContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
})

const OptionsList = styled('div')({
  maxHeight: '300px',
  overflowY: 'auto',
})

const OptionItem = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  cursor: 'pointer',
  '&:hover': {
    background: colorPalette.monotone[1],
  },
  borderRadius: '4px',
  padding: '2px 0',
})

const LoadingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '20px 0',
})

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const Spinner = styled('div')({
  width: '28px',
  height: '28px',
  border: `3px solid ${colorPalette.monotone[2]}`,
  borderTop: `3px solid ${colorPalette.skyBlue[7]}`,
  borderRadius: '50%',
  animation: `${spin} 0.8s linear infinite`,
})

const ApplyButtonContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '8px',
})

export default ProcessScopeSearchSelectPopper
